import fieldConstants from './constants/rmaFieldConstants.json';

const SERIAL_POLICY_OPTIONS = Array.isArray(fieldConstants.serialPolicies)
  ? fieldConstants.serialPolicies
  : [];
const RECEIPT_STATUS_OPTIONS = Array.isArray(fieldConstants.receiptStatuses)
  ? fieldConstants.receiptStatuses
  : [];
const DEFECT_TYPE_OPTIONS = Array.isArray(fieldConstants.defectTypes)
  ? fieldConstants.defectTypes
  : [];

const SHIPMENT_TYPE_OPTIONS = [
  { value: 'Full', label: 'Full unit (Display + Box)' },
  { value: 'OnlyDisplay', label: 'Only Display (DIU)' },
  { value: 'OnlyBox', label: 'Only Box (ELU)' }
];
const SHIPMENT_TYPE_DEFAULT = SHIPMENT_TYPE_OPTIONS[0]?.value || 'Full';

const MOUNT_TYPE_OPTIONS = [
  { value: 'PanelMount', label: 'Panel Mount' },
  { value: 'Hinge', label: 'Hinge' }
];
const MOUNT_TYPE_DEFAULT = MOUNT_TYPE_OPTIONS[0]?.value || 'PanelMount';

const ACCESSORY_FIELDS = [
  { key: 'acPowerCableQty', label: 'AC power cable' },
  { key: 'hdmiCableQty', label: 'HDMI cable' },
  { key: 'dpCableQty', label: 'DP cable' },
  { key: 'touchCableQty', label: 'Touch cable (USB A–USB B)' }
];

const SERIAL_POLICY_DEFAULT =
  SERIAL_POLICY_OPTIONS.find(option => option.value === 'unknown')?.value ||
  SERIAL_POLICY_OPTIONS[0]?.value ||
  '';
const RECEIPT_STATUS_DEFAULT =
  RECEIPT_STATUS_OPTIONS.find(option => option.value === 'ok')?.value ||
  RECEIPT_STATUS_OPTIONS[0]?.value ||
  '';

const OTHER_DEFECT_VALUE =
  DEFECT_TYPE_OPTIONS.find(option => option.value === 'other')?.value ||
  '';

export {
  SERIAL_POLICY_OPTIONS,
  RECEIPT_STATUS_OPTIONS,
  DEFECT_TYPE_OPTIONS,
  SHIPMENT_TYPE_OPTIONS,
  MOUNT_TYPE_OPTIONS,
  ACCESSORY_FIELDS,
  SERIAL_POLICY_DEFAULT,
  RECEIPT_STATUS_DEFAULT,
  OTHER_DEFECT_VALUE,
  SHIPMENT_TYPE_DEFAULT,
  MOUNT_TYPE_DEFAULT
};

export function optionLabel(options, value) {
  if (!Array.isArray(options)) return value;
  const match = options.find(option => option.value === value);
  return match ? match.label : value;
}

export function sanitizeSerialPolicy(value) {
  const match = SERIAL_POLICY_OPTIONS.find(option => option.value === value);
  return match ? match.value : SERIAL_POLICY_DEFAULT;
}

export function sanitizeReceiptStatus(value) {
  const match = RECEIPT_STATUS_OPTIONS.find(option => option.value === value);
  return match ? match.value : RECEIPT_STATUS_DEFAULT;
}

export function sanitizeDefectType(value) {
  const match = DEFECT_TYPE_OPTIONS.find(option => option.value === value);
  if (match) return match.value;
  return OTHER_DEFECT_VALUE || (DEFECT_TYPE_OPTIONS[0]?.value || '');
}

export function toEditorItems(items, fallbackQuantity = 1) {
  const source = Array.isArray(items) && items.length
    ? items
    : [{ name: '', quantity: fallbackQuantity, serials: [] }];

  return source.map(item => {
    const quantityValue = Number(item?.quantity);
    const quantityString = Number.isFinite(quantityValue)
      ? String(quantityValue)
      : String(fallbackQuantity);
    const serialsText =
      typeof item?.serialsText === 'string'
        ? item.serialsText
        : Array.isArray(item?.serials)
        ? item.serials
            .map(serial => String(serial ?? '').trim())
            .filter(serial => serial !== '')
            .join('\n')
        : '';

    return {
      name: typeof item?.name === 'string' ? item.name : '',
      quantity: quantityString,
      serialsText
    };
  });
}

export function editorItemsToPayload(items, { allowZeroQuantity = false } = {}) {
  if (!Array.isArray(items)) return [];
  return items
    .map(item => {
      if (!item || typeof item !== 'object') return null;
      const name = typeof item.name === 'string' ? item.name.trim() : '';
      const quantityNumber = Number(item.quantity);
      let quantity = Number.isFinite(quantityNumber) ? quantityNumber : 0;
      if (!allowZeroQuantity && quantity <= 0) {
        quantity = 1;
      }
      if (allowZeroQuantity && quantity < 0) {
        quantity = 0;
      }
      const serialsText = typeof item.serialsText === 'string' ? item.serialsText : '';
      const serials = serialsText
        .split(/[\r\n,;]+/)
        .map(serial => serial.trim())
        .filter(serial => serial !== '');
      if (!name && serials.length === 0 && quantity === 0) {
        return null;
      }
      return { name, quantity, serials };
    })
    .filter(Boolean);
}

export function sanitizeShipmentType(value) {
  const candidate = typeof value === 'string' ? value : '';
  return SHIPMENT_TYPE_OPTIONS.some(option => option.value === candidate)
    ? candidate
    : SHIPMENT_TYPE_DEFAULT;
}

export function sanitizeMountType(value) {
  const candidate = typeof value === 'string' ? value : '';
  return MOUNT_TYPE_OPTIONS.some(option => option.value === candidate)
    ? candidate
    : MOUNT_TYPE_DEFAULT;
}

function sanitizeNonNegative(value, fallback = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return fallback;
  }
  return parsed;
}

function sanitizePositiveQuantity(value, fallback = 1) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallback;
  }
  return parsed;
}

export function normalizeMount(mount) {
  const source = mount && typeof mount === 'object' ? mount : {};
  return {
    type: sanitizeMountType(source.type),
    hmi: source.hmi === false ? false : true,
    qty: sanitizeNonNegative(source.qty, 1)
  };
}

export function normalizeAccessories(accessories) {
  const source = accessories && typeof accessories === 'object' ? accessories : {};
  return {
    acPowerCableQty: sanitizeNonNegative(source.acPowerCableQty),
    hdmiCableQty: sanitizeNonNegative(source.hdmiCableQty),
    dpCableQty: sanitizeNonNegative(source.dpCableQty),
    touchCableQty: sanitizeNonNegative(source.touchCableQty)
  };
}

export function normalizeExpectedComposition(expectedReturn = {}) {
  const shipmentType = sanitizeShipmentType(expectedReturn.shipmentType);
  const deviceQuantity = sanitizePositiveQuantity(expectedReturn.deviceQuantity, 1);
  const mountSource =
    expectedReturn && typeof expectedReturn.mount === 'object' ? expectedReturn.mount : null;
  const mountedFlag = Boolean(expectedReturn?.mounted) || Boolean(mountSource);
  const mount = mountedFlag ? normalizeMount(mountSource) : null;
  const accessories = normalizeAccessories(expectedReturn?.accessories);
  return {
    shipmentType,
    deviceQuantity,
    mounted: Boolean(mountedFlag && mount),
    mount,
    accessories
  };
}

export function normalizeReceiptComposition(receiptIntake = {}, fallbackExpected) {
  const expected = fallbackExpected || normalizeExpectedComposition();
  const shipmentType = sanitizeShipmentType(
    receiptIntake.shipmentType ?? expected.shipmentType
  );
  const deviceQuantity = sanitizePositiveQuantity(
    receiptIntake.deviceQuantity ?? expected.deviceQuantity,
    1
  );
  const mountProvided =
    typeof receiptIntake.mounted !== 'undefined' || typeof receiptIntake.mount === 'object';
  const mountSource = mountProvided
    ? receiptIntake.mount
    : expected.mount;
  const mountedFlag = mountProvided
    ? Boolean(receiptIntake.mounted) || Boolean(receiptIntake.mount)
    : expected.mounted;
  const mount = mountedFlag ? normalizeMount(mountSource) : null;
  const accessories = normalizeAccessories(
    mountProvided || receiptIntake.accessories ? receiptIntake.accessories : expected.accessories
  );
  return {
    shipmentType,
    deviceQuantity,
    mounted: Boolean(mountedFlag && mount),
    mount,
    accessories
  };
}

export function accessoriesIncluded(accessories) {
  if (!accessories || typeof accessories !== 'object') {
    return false;
  }
  return ACCESSORY_FIELDS.some(({ key }) => Number(accessories[key] ?? 0) > 0);
}

export function compositionsMatch(expected, received) {
  if (!expected || !received) return false;
  if (sanitizeShipmentType(expected.shipmentType) !== sanitizeShipmentType(received.shipmentType)) {
    return false;
  }
  if (sanitizePositiveQuantity(expected.deviceQuantity, 1) !== sanitizePositiveQuantity(received.deviceQuantity, 1)) {
    return false;
  }
  const expectedMounted = Boolean(expected.mounted) && expected.mount;
  const receivedMounted = Boolean(received.mounted) && received.mount;
  if (expectedMounted !== receivedMounted) {
    return false;
  }
  if (expectedMounted && receivedMounted) {
    const expectedMount = normalizeMount(expected.mount);
    const receivedMount = normalizeMount(received.mount);
    if (expectedMount.type !== receivedMount.type) return false;
    if (Boolean(expectedMount.hmi) !== Boolean(receivedMount.hmi)) return false;
    if (sanitizeNonNegative(expectedMount.qty, 1) !== sanitizeNonNegative(receivedMount.qty, 1)) {
      return false;
    }
  }
  const expectedAccessories = normalizeAccessories(expected.accessories);
  const receivedAccessories = normalizeAccessories(received.accessories);
  return ACCESSORY_FIELDS.every(({ key }) => {
    return sanitizeNonNegative(expectedAccessories[key]) === sanitizeNonNegative(receivedAccessories[key]);
  });
}

function buildItemKey(item, index) {
  const nameKey = (item?.name || '').trim().toLowerCase();
  if (nameKey) return `name:${nameKey}`;
  const serialsKey = Array.isArray(item?.serials)
    ? item.serials
        .map(serial => String(serial || '').trim().toLowerCase())
        .filter(Boolean)
        .join('|')
    : '';
  if (serialsKey) return `serials:${serialsKey}`;
  return `row:${index}`;
}

function aggregateQuantities(items) {
  const map = new Map();
  if (!Array.isArray(items)) {
    return map;
  }
  items.forEach((item, index) => {
    if (!item || typeof item !== 'object') return;
    const quantityValue = Number(item.quantity);
    const quantity = Number.isFinite(quantityValue) ? quantityValue : 0;
    const key = buildItemKey(item, index);
    const nextQuantity = (map.get(key) || 0) + quantity;
    map.set(key, nextQuantity);
  });
  return map;
}

export function hasItemMismatch(expectedItems = [], receivedItems = []) {
  const expectedMap = aggregateQuantities(expectedItems);
  const receivedMap = aggregateQuantities(receivedItems);
  if (expectedMap.size !== receivedMap.size) {
    return true;
  }
  for (const [key, expectedQty] of expectedMap.entries()) {
    if (!receivedMap.has(key)) {
      return true;
    }
    if (receivedMap.get(key) !== expectedQty) {
      return true;
    }
  }
  return false;
}

export function buildExpectedReturnPayload(items, serialPolicy, notes, previous = {}) {
  const sanitizedItems = editorItemsToPayload(items, { allowZeroQuantity: false });
  const policy = sanitizeSerialPolicy(serialPolicy);
  const trimmedNotes = typeof notes === 'string' ? notes.trim() : '';
  const normalized = normalizeExpectedComposition(previous);
  const accessories = normalizeAccessories(normalized.accessories);
  const payload = {
    items: sanitizedItems,
    serialsPolicy: policy,
    notes: trimmedNotes,
    shipmentType: normalized.shipmentType,
    deviceQuantity: normalized.deviceQuantity,
    mounted: normalized.mounted,
    accessories
  };
  payload.mount = normalized.mounted && normalized.mount ? normalized.mount : null;
  return payload;
}
